import React from 'react';
import { Helmet } from 'react-helmet-async';
import PageHeader from '../components/PageHeader';

const LAST_UPDATED = 'June 1, 2025';

function SmsPolicy() {
	return (
		<>
			<Helmet>
				<title>SMS Policy | A-Z Home Improvement LLC</title>
				<meta name="description" content="SMS messaging policy for A-Z Home Improvement LLC. Learn about message types, opt-in and opt-out procedures, and how we use text communications." />
			</Helmet>
			<PageHeader title="SMS Messaging Policy" subtitle={`Last updated: ${LAST_UPDATED}`} />

			<section className="section">
				<div className="container" style={{ maxWidth: 760 }}>
					<h2>1. Who Sends These Messages</h2>
					<p>A-Z Home Improvement LLC ("A-Z," "we," "us," or "our") sends SMS (text message) communications to customers and prospective customers who have opted in to receive such messages. Our business is located at 3842 W. McDowell Rd, Suite 110, Phoenix, AZ 85009.</p>

					<h2>2. How to Opt In</h2>
					<p>You provide consent to receive SMS messages from A-Z Home Improvement LLC by:</p>
					<ul>
						<li>Checking the SMS consent checkbox on our website contact form and submitting your request,</li>
						<li>Texting a keyword to our designated number as instructed in a promotional communication, or</li>
						<li>Providing your phone number verbally or in writing to a company representative with explicit consent noted.</li>
					</ul>
					<p>By opting in, you confirm that you are the account holder or have permission to opt in the phone number provided.</p>

					<h2>3. Types of Messages We Send</h2>
					<p>Once you opt in, you may receive the following types of messages from A-Z Home Improvement LLC:</p>
					<ul>
						<li><strong>Estimate confirmations:</strong> Confirmation of your free estimate appointment date, time, and team member.</li>
						<li><strong>Appointment reminders:</strong> Reminder messages before scheduled consultations or project start dates.</li>
						<li><strong>Project status updates:</strong> Updates about your active renovation project, including start times and milestone completions.</li>
						<li><strong>Promotional messages:</strong> Seasonal promotions, discounts on services, referral program offers, and special financing announcements relevant to home remodeling.</li>
						<li><strong>Follow-up communications:</strong> Post-project satisfaction check-ins and warranty reminders.</li>
					</ul>

					<h2>4. Message Frequency</h2>
					<p>Message frequency varies by your project status. During an active project, you may receive up to 6 messages per week. During non-active months, expect no more than 2–4 promotional messages per month. You will never receive unsolicited messages unrelated to our services.</p>

					<h2>5. Message and Data Rates</h2>
					<p>A-Z Home Improvement LLC does not charge for text messages. However, <strong>message and data rates from your mobile carrier may apply.</strong> Contact your carrier for information about your specific plan's messaging charges.</p>

					<h2>6. How to Opt Out (STOP)</h2>
					<p>You may opt out of SMS communications from A-Z Home Improvement LLC at any time by:</p>
					<ul>
						<li>Replying <strong>STOP</strong> to any text message from us. You will receive one final confirmation that you have been removed, and no further messages will be sent.</li>
						<li>Emailing <a href="mailto:support@azhomefundingservices.com" style={{ color: 'var(--brand)' }}>support@azhomefundingservices.com</a> with "SMS Opt-Out" in the subject line.</li>
						<li>Calling us at <a href="tel:+16023902536" style={{ color: 'var(--brand)' }}>(602) 390-2536</a> during business hours.</li>
						<li>Using the opt-out form at <a href="/opt-out" style={{ color: 'var(--brand)' }}>azhomefundingservices.com/opt-out</a>.</li>
					</ul>
					<p>After opting out, you may re-subscribe by texting START or by re-submitting consent through our website form.</p>

					<h2>7. How to Get Help (HELP)</h2>
					<p>Reply <strong>HELP</strong> to any message for instructions. You can also contact us at:</p>
					<ul>
						<li>Email: <a href="mailto:support@azhomefundingservices.com" style={{ color: 'var(--brand)' }}>support@azhomefundingservices.com</a></li>
						<li>Phone: <a href="tel:+16023902536" style={{ color: 'var(--brand)' }}>(602) 390-2536</a></li>
					</ul>

					<h2>8. Privacy and Data Sharing</h2>
					<p>We do not sell, rent, trade, or share your phone number or opt-in information with third parties for their marketing purposes. No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. Your data may be processed by SMS platform providers solely as needed to deliver messages on our behalf, under confidentiality obligations. See our full <a href="/privacy" style={{ color: 'var(--brand)' }}>Privacy Policy</a> for details.</p>

					<h2>9. Carrier Support</h2>
					<p>SMS messaging services are available on AT&amp;T, Verizon, T-Mobile, and other major US carriers. Carriers are not liable for delayed or undelivered messages.</p>

					<h2>10. Changes to This Policy</h2>
					<p>We may update this SMS Policy from time to time. The updated date at the top of this page indicates when changes were last made. Continued acceptance of messages after changes constitutes acceptance of the revised policy.</p>

					<h2>11. Contact Us</h2>
					<address style={{ fontStyle: 'normal', background: '#f4f4f4', borderRadius: 8, padding: 20, marginTop: 10 }}>
						<strong>A-Z Home Improvement LLC</strong><br />
						3842 W. McDowell Rd, Suite 110<br />
						Phoenix, AZ 85009<br />
						Phone: <a href="tel:+16023902536" style={{ color: 'var(--brand)' }}>(602) 390-2536</a><br />
						Email: <a href="mailto:support@azhomefundingservices.com" style={{ color: 'var(--brand)' }}>support@azhomefundingservices.com</a>
					</address>
				</div>
			</section>
		</>
	);
}

export default SmsPolicy;
